import { useMemo, useRef, useState, type ReactNode } from 'react'
import { PageTrailContext, type TrailApi, type TrailItem, type TrailState } from '@/hooks/usePageTrail'

/**
 * Holds the depth trail the retained top bar renders (round 18, frame `18b`).
 * The hooks that read and write it live in `hooks/usePageTrail.ts`.
 *
 * `publish`/`release` are built ONCE and never re-created. If they were
 * memoized on `trail` they would change identity on every publish, and a
 * consumer with them in its dependency array would publish → re-render →
 * publish, forever.
 *
 * Ownership is tracked by identity rather than by a counter: `release` clears
 * only when the caller still owns the trail, so a page unmounting behind an
 * exit animation cannot wipe the trail the next page just published.
 */
export function PageTrailProvider({ children }: { children: ReactNode }) {
  const [trail, setTrail] = useState<TrailItem[]>([])
  const ownerRef = useRef<object | null>(null)

  const api = useMemo<TrailApi>(
    () => ({
      publish: (owner, items) => {
        ownerRef.current = owner
        setTrail(items ?? [])
      },
      release: (owner) => {
        if (ownerRef.current !== owner) return
        ownerRef.current = null
        setTrail([])
      },
    }),
    []
  )

  const value = useMemo<TrailState>(() => ({ trail, ...api }), [trail, api])

  return <PageTrailContext.Provider value={value}>{children}</PageTrailContext.Provider>
}
